import React, { useCallback, useRef, useState } from 'react';

import { graphql, Link, PageProps } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Grid2 as Grid, styled, useTheme } from '@mui/material';

import {
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  Line,
  ResponsiveGrid,
  ResponsiveTabs,
  ServiceTeaser,
} from '@mewa/ui';

import { localeBaseUrl } from '../../libs/alternative-links';
import { mewaCircleVariantsMap } from '../../libs/mewa-circle-variants';
import t from '../../libs/translate';
import { useConversionButtonLabel } from '../a-b-testing/cta-label-experiment';
import { NavigationData } from '../components/header/types';
import { CollectionSliderWrapper } from '../components/slider/collection-slider-wrapper';
import { ProductsSliderContainer } from '../components/slider/products-slider';
import { industryCardImages, jpgPath } from '../helpers/image-factory';
import { groupByCollection } from '../helpers/product-grouping';
import { protectiveClothingIconForType } from '../helpers/protective-clothing-icons';
import { GlobalPageContext } from '../types';
import { IndustryHeroScroller } from './components/industry-hero-scroller';
import { Industry, ProductGroup } from './components/industry-page-types';
import { IndustryStickyTabs } from './components/industry-sticky-tabs';
import { StrapiMewaCircleCertainVariant } from './dynamic-content-page/content-components/mewa-circle-certain-variant';

export { Head } from '../layouts/head';

type Props = NavigationData & {
  pimIndustryServiceSegment: {
    industry: Industry;
    productGroups: ProductGroup[];
    serviceSegment: {
      name: string;
      slug: string;
      key: ServiceSegmentType;
    };
  };
  translations: {
    service_segment_groups_dropdown_label: string;
    collection_slider_collection_link_label: string;
    show_product_details_label: string;
    mewa_principle_main_title: string;
    navigation_product_menu_label: string;
  };
  logoTeaser?: ServiceTeaserData<ServiceTeaserType.logoEmbroideryService>;
};

type PageContext = GlobalPageContext & {
  id: string;
};

export const query = graphql`
  query ($id: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    pimIndustryServiceSegment(id: { eq: $id }) {
      industry {
        name
        slug
        h1
        h2
        infoText
        serviceSegments {
          category
          name
          slug
        }
        assets {
          default
          keyVisualClothingDesktop
          keyVisualClothingMobile
          keyVisualNonClothingDesktop
          keyVisualNonClothingMobile
        }
      }

      productGroups {
        name
        typeName
        products {
          ...ProductData
        }
      }

      serviceSegment {
        name
        slug
        key
      }
    }

    translations: translation(locale: { eq: $locale }) {
      service_segment_groups_dropdown_label
      collection_slider_collection_link_label
      show_product_details_label
      mewa_principle_main_title
      navigation_product_menu_label
    }

    logoTeaser: strapiServiceTeaser(
      ServiceType: { eq: "logo-stick-service" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }
  }
`;

const FADE_DURATION = 400;
const PAUSE = 100;

const ProductIndustriesClothing = ({
  data: {
    pimIndustryServiceSegment: pageData,
    translations,
    logoTeaser,
    headerAndFooterTranslations,
    navigationLinks,
  },
  pageContext: { langPrefix, locale, featureDev, market },
}: PageProps<Props, PageContext>) => {
  const theme = useTheme();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [renderGroupIndex, setRenderGroupIndex] = useState(0);
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const handleTabChange = useCallback(
    (newValue: number) => {
      setActiveGroupIndex(newValue);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setRenderGroupIndex(newValue);
      }, FADE_DURATION + PAUSE);
    },
    [setActiveGroupIndex, setRenderGroupIndex],
  );

  const clothing = pageData.industry.serviceSegments.filter(
    (s) => s.category === 'MBS',
  );

  const nonClothing = pageData.industry.serviceSegments.filter(
    (s) => s.category !== 'MBS',
  );

  const indexOfFirstNonClothingSegment =
    pageData.industry.serviceSegments.findIndex((s) => s.category !== 'MBS');

  const tabLinks = clothing.map((serviceSegment) => ({
    label: serviceSegment.name,
    to: `${langPrefix}/${pageData.industry.slug}-${serviceSegment.slug}`,
  }));

  if (nonClothing.length > 0) {
    const navLinks = navigationLinks.navigation;
    // insert the non clothing link at the position of the first non clothing service segment
    tabLinks.splice(indexOfFirstNonClothingSegment, 0, {
      label: navLinks.nonClothing.name,
      to: `${langPrefix}/${pageData.industry.slug}-${navLinks.nonClothing.slug}`,
    });
  }

  let activeTabIndex = clothing.findIndex(
    (serviceSegment) => serviceSegment.slug === pageData.serviceSegment.slug,
  );

  if (activeTabIndex < 0) {
    activeTabIndex = tabLinks.length - 1;
  }

  const groups = pageData.productGroups.map((group) => ({
    ...group,
    icon: protectiveClothingIconForType(group.typeName),
  }));

  const productsGroupedByCollection = groupByCollection(
    groups[renderGroupIndex].products,
  );

  const conversion = {
    label: ctaLabel,
    link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
  };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    {
      name: navigationLinks.navigation.industries.name,
      slug: `${langPrefix}/${navigationLinks.navigation.industries.slug}/`,
    },
    {
      name: pageData.industry.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.industries.name,
    slug: `${langPrefix}/${navigationLinks.navigation.industries.slug}/`,
  };

  return (
    <>
      <div
        data-pagefind-meta={`industry_name:${pageData.industry.name}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`service_segment_name:${pageData.serviceSegment.name}`}
        style={{ visibility: 'hidden' }}
      />
      <div
        data-pagefind-meta={`image:${
          industryCardImages(pageData.industry.assets.default)[0].src
        }`}
        style={{ visibility: 'hidden' }}
      />
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <IndustryHeroScroller
        title={pageData.industry.name}
        subtitle={pageData.industry.h1 ?? ''}
        subClaim={pageData.industry.h2 ?? ''}
        introText={pageData.industry.infoText ?? ''}
        cta={conversion}
        backgroundImage={[
          {
            src: jpgPath(
              pageData.industry.assets.keyVisualClothingMobile,
              // TODO: kind of large for mobile (the scaled version of the
              // image factory looks pixelated unfortunately). We might need a
              // smaller mobile optimized Version of the image.
              1080,
              1728,
            ),
            media: `(max-width: ${theme.breakpoints.values.sm - 1}px)`,
            width: 1080,
            height: 1728,
          },
          {
            src: jpgPath(
              pageData.industry.assets.keyVisualClothingDesktop,
              1920,
              823,
            ),
            media: `(min-width: ${theme.breakpoints.values.sm}px)`,
            width: 1920,
            height: 823,
          },
        ]}
      />
      <IndustryStickyTabs links={tabLinks} selectedIndex={activeTabIndex} />

      <ResponsiveGrid
        sx={{
          marginTop: '52px',

          [theme.breakpoints.up('sm')]: {
            marginTop: '60px',
          },
        }}
      >
        <Grid size={12}>
          {groups.length > 1 && (
            <StyledTabs
              name="service-segment-tabs"
              selectLabel={t(
                translations,
                'service_segment_groups_dropdown_label',
              )}
              activeIndex={activeGroupIndex}
              onChange={handleTabChange}
              items={groups}
            />
          )}
        </Grid>
      </ResponsiveGrid>

      <FadeContainer
        style={{ opacity: activeGroupIndex === renderGroupIndex ? 1 : 0 }}
      >
        {productsGroupedByCollection.map((collection, cIdx) => (
          <ProductsSliderContainer key={`${renderGroupIndex}-${cIdx}`}>
            <CollectionSliderWrapper
              collection={collection}
              langPrefix={langPrefix}
              collectionLinkLabel={t(
                translations,
                'collection_slider_collection_link_label',
              )}
              detailsPageLabel={t(translations, 'show_product_details_label')}
            />
            {cIdx === 2 && logoTeaser && (
              <ServiceTeaser
                data-pagefind-ignore
                description={logoTeaser.description}
                title={logoTeaser.title}
                type={logoTeaser.serviceType}
              />
            )}
          </ProductsSliderContainer>
        ))}
        {productsGroupedByCollection.length < 3 && logoTeaser && (
          <ServiceTeaser
            data-pagefind-ignore
            description={logoTeaser.description}
            title={logoTeaser.title}
            type={logoTeaser.serviceType}
          />
        )}
      </FadeContainer>

      {productsGroupedByCollection.length <= 3 && (
        <ResponsiveGrid>
          <Grid size={12}>
            <Line
              sx={{
                zIndex: 1,
                transform: [
                  'translateY(43px)',
                  null,
                  null,
                  'translateY(113px)',
                ],
              }}
            />
          </Grid>
        </ResponsiveGrid>
      )}
      <ResponsiveGrid>
        <Grid size={12}>
          <StrapiMewaCircleCertainVariant
            circleVariant={mewaCircleVariantsMap[pageData.serviceSegment.key]}
            locale={locale}
            title={t(translations, 'mewa_principle_main_title')}
            link={
              <Link
                to={`${langPrefix || ''}/${navigationLinks.fullService.slug}`}
              >
                {navigationLinks.fullService.name}
              </Link>
            }
          />
        </Grid>
      </ResponsiveGrid>
    </>
  );
};

export default ProductIndustriesClothing;

const StyledTabs = styled(ResponsiveTabs)(({ theme }) => ({
  marginBottom: 0,

  [theme.breakpoints.up('sm')]: {
    marginBottom: '58px',
  },
}));

const FadeContainer = styled('div')({
  transition: `opacity ${FADE_DURATION}ms ease`,
});
